/**
 * Admin: Emoji Manager — v3 category-first single-panel layout.
 *
 * Removed (per user request — packs were clutter):
 *   • Pack picker sidebar
 *   • "Pick a pack to upload" gating
 *   • Pack create/edit/delete UI
 *
 * Kept:
 *   • Per-emoji preview-on-each-platform dialog (sparkles button)
 *   • Per-emoji edit (label + categories)
 *   • Per-emoji delete + bulk-delete + nuclear "reset everything"
 *   • Image audit (broken-file finder)
 *
 * New:
 *   • Quick upload at top — drop files + category tags, done
 *   • Flat searchable / category-filterable emoji grid
 *   • "Start fresh — delete all" big red button (with double-confirm)
 *
 * Backend dependencies:
 *   POST /api/admin/emojis/upload-direct        (pack-less upload)
 *   GET  /api/admin/emojis/all                  (flat list)
 *   POST /api/admin/emojis/bulk-delete          (batch + purge)
 *   POST /api/admin/emojis/audit-images         (find broken files)
 *   PUT  /api/admin/emojis/{id}                 (label / categories edit)
 *   DELETE /api/admin/emojis/{id}               (single delete)
 *   POST /api/admin/emojis/{id}/auto-detect-bad-platforms
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Trash2, Edit3, Upload, Loader2, Smile, ExternalLink,
  Save, X as XIcon, Sparkles, Search, AlertTriangle, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';
import { EmojiPlatformPreviewDialog } from '@/components/admin/EmojiPlatformPreviewDialog';
import { BatchUploadPanel } from '@/components/admin/BatchUploadPanel';
import { BatchAutoDetectDialog } from '@/components/admin/BatchAutoDetectDialog';

const API = process.env.REACT_APP_BACKEND_URL;


export const EmojiManagerTab = () => {
  const [emojis, setEmojis] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload state — uploadCategories is the batch default applied to
  // every dropped file. Per-file overrides happen inside BatchUploadPanel.
  const [uploadCategories, setUploadCategories] = useState('');

  // Filter / search
  const [filterCategory, setFilterCategory] = useState('all');
  const [search, setSearch] = useState('');

  // Edit / preview state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ label: '', categories: '' });
  const [previewEmoji, setPreviewEmoji] = useState(null);

  // Bulk-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Batch auto-detect dialog
  const [batchAutoOpen, setBatchAutoOpen] = useState(false);

  const reload = useCallback(async () => {
    const { data, error } = await apiFetch('/api/admin/emojis/all');
    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }
    setEmojis(data?.emojis || []);
    setLoading(false);
  }, []);

  // Initial load — inlined fetch instead of useEffect(() => reload()) so
  // the linter doesn't flag the indirect setState. Same result.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await apiFetch('/api/admin/emojis/all');
      if (cancelled) return;
      if (error) {
        toast.error(error);
      } else {
        setEmojis(data?.emojis || []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Distinct category list (from the loaded data — no extra request needed)
  const categories = useMemo(() => {
    const counts = {};
    for (const e of emojis) {
      for (const c of e.categories || []) counts[c] = (counts[c] || 0) + 1;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [emojis]);

  // Client-side filter
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return emojis.filter((e) => {
      const matchCat = filterCategory === 'all' || (e.categories || []).includes(filterCategory);
      const matchTerm = !term || (e.label || '').toLowerCase().includes(term);
      return matchCat && matchTerm;
    });
  }, [emojis, filterCategory, search]);

  const handleSaveEdit = async (emoji) => {
    const cats = editDraft.categories.split(',').map((c) => c.trim()).filter(Boolean);
    const { error } = await apiFetch(`/api/admin/emojis/${emoji.emoji_id}`, {
      method: 'PUT',
      body: { label: editDraft.label || emoji.label, categories: cats },
    });
    if (error) { toast.error(error); return; }
    toast.success('Saved');
    setEditingId(null);
    reload();
  };

  const handleDelete = async (emoji) => {
    if (!window.confirm(`Delete "${emoji.label}"? This removes the record + the source file from disk.`)) return;
    const { error } = await apiFetch('/api/admin/emojis/bulk-delete', {
      method: 'POST',
      body: { emoji_ids: [emoji.emoji_id] },
    });
    if (error) { toast.error(error); return; }
    toast.success('Deleted');
    reload();
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected emojis? This removes records + source files from disk.`)) return;
    const { error } = await apiFetch('/api/admin/emojis/bulk-delete', {
      method: 'POST',
      body: { emoji_ids: Array.from(selectedIds) },
    });
    if (error) { toast.error(error); return; }
    toast.success(`Deleted ${selectedIds.size} emoji(s)`);
    setSelectedIds(new Set());
    reload();
  };

  const handlePurgeAll = async () => {
    if (!window.confirm('PURGE every emoji + every legacy pack + every unlock record? This is destructive and cannot be undone.')) return;
    if (!window.confirm('Are you absolutely sure? Type-test: the next OK truly wipes everything.')) return;
    const { data, error } = await apiFetch('/api/admin/emojis/bulk-delete', {
      method: 'POST',
      body: { purge_all: true },
    });
    if (error) { toast.error(error); return; }
    toast.success(`Purged: ${data.emojis_deleted} emojis · ${data.packs_deleted} legacy packs · ${data.files_unlinked} files. Fresh start.`);
    setSelectedIds(new Set());
    reload();
  };

  return (
    <div className="space-y-4" data-testid="emoji-manager-tab">
      {/* Header card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Smile className="h-4 w-4 text-[#00A896]" /> Communication Emojis Manager
          </CardTitle>
          <CardDescription className="text-xs">
            Upload emojis with category tags. Users build their own 6-emoji packs on the public{' '}
            <a href="/communication-emojis" target="_blank" rel="noreferrer" className="text-[#00A896] underline">
              /communication-emojis <ExternalLink className="inline h-2.5 w-2.5" />
            </a>{' '}
            page — each downloaded ZIP contains the picked emojis resized for every platform (Slack, Discord, Teams, Twitch, Zoom, Telegram, WhatsApp, etc.) + install instructions. The legacy &ldquo;packs&rdquo; concept has been removed; categories are the only organisation axis.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Batch upload — drag-and-drop + per-file review */}
      <Card data-testid="emoji-quick-upload-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="h-4 w-4 text-[#00A896]" /> Upload emojis
          </CardTitle>
          <CardDescription className="text-xs">
            Drop multiple files at once · per-file label &amp; category overrides · 2 MB per file · auto-resized for all 13 platforms.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="upload-categories" className="text-xs font-medium">
              Default category tags <span className="text-muted-foreground font-normal">(applied to every new file you drop · per-file override available in the staged list)</span>
            </Label>
            <Input
              id="upload-categories"
              value={uploadCategories}
              onChange={(e) => setUploadCategories(e.target.value)}
              placeholder="reactions, mood, workplace"
              className="text-sm"
              data-testid="upload-categories-input"
            />
          </div>
          <BatchUploadPanel
            defaultCategories={uploadCategories}
            onComplete={({ ok }) => { if (ok > 0) {
              // Refresh the grid after a successful batch — inlined fetch
              // so we don't rely on a closure-captured stale `reload`.
              (async () => {
                const { data } = await apiFetch('/api/admin/emojis/all');
                if (data) setEmojis(data.emojis || []);
              })();
            }}}
          />
        </CardContent>
      </Card>

      {/* Maintenance — fix broken images + purge everything */}
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardContent className="py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-medium">Maintenance</p>
            <p className="text-xs text-muted-foreground">
              <b>Audit images</b> — find emojis whose source file is missing. <b>Delete broken only</b> — prune just the broken rows; working emojis are kept. <b>Purge everything</b> — nuclear reset for starting over.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500/40 hover:bg-amber-500/10"
              onClick={async () => {
                const { data, error } = await apiFetch('/api/admin/emojis/audit-images', {
                  method: 'POST', body: { hide_broken: false },
                });
                if (error) return toast.error(error);
                if (data.broken_count === 0) {
                  toast.success(`All ${data.total_emojis} emojis look good.`);
                } else {
                  toast.warning(`Found ${data.broken_count} broken / ${data.total_emojis} total. Check console for IDs.`);
                  console.log('Broken emojis:', data.broken);
                }
              }}
              data-testid="emoji-audit-btn"
            >
              Audit images
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500/40 hover:bg-amber-500/10"
              onClick={async () => {
                if (!window.confirm('Hide every emoji whose source file is missing? Sets image_path="" so the public gallery skips them.')) return;
                const { data, error } = await apiFetch('/api/admin/emojis/audit-images', {
                  method: 'POST', body: { hide_broken: true },
                });
                if (error) return toast.error(error);
                toast.success(`Hidden ${data.hidden} broken emoji(s).`);
                reload();
              }}
              data-testid="emoji-audit-hide-btn"
            >
              Audit + hide broken
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500/60 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
              onClick={async () => {
                if (!window.confirm('Delete every emoji record whose source file is missing? This removes the rows only — working emojis are kept. This cannot be undone.')) return;
                const { data, error } = await apiFetch('/api/admin/emojis/delete-broken', {
                  method: 'POST', body: {},
                });
                if (error) return toast.error(error);
                if (data.deleted === 0) {
                  toast.success(`Nothing to clean up — all ${data.total_emojis} emoji(s) look good.`);
                } else {
                  toast.success(`Deleted ${data.deleted} broken emoji record(s).`);
                  console.log('Deleted broken emojis:', data.deleted_emojis);
                }
                reload();
              }}
              data-testid="emoji-delete-broken-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete broken only
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-500/50 text-red-600 hover:bg-red-500/10"
              onClick={handlePurgeAll}
              data-testid="emoji-purge-all-btn"
            >
              <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Purge everything (start fresh)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter + grid */}
      <Card>
        <CardHeader className="pb-2 space-y-3">
          <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
            <span>All emojis ({emojis.length})</span>
            {selectedIds.size > 0 && (
              <div className="flex gap-2 items-center flex-wrap">
                <Badge>{selectedIds.size} selected</Badge>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-purple-500/40 text-purple-700 dark:text-purple-300 hover:bg-purple-500/10"
                  onClick={() => setBatchAutoOpen(true)}
                  data-testid="emoji-batch-auto-detect-btn"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> Auto-detect on selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-500/50 text-red-600 hover:bg-red-500/10"
                  onClick={handleBulkDelete}
                  data-testid="emoji-bulk-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete selected
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                  Clear
                </Button>
              </div>
            )}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search labels…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 text-sm h-9"
                data-testid="emoji-search-input"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setFilterCategory('all')}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${
                  filterCategory === 'all'
                    ? 'bg-[#00E5CC] text-[#0d1f2d]'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
                data-testid="filter-chip-all"
              >
                All ({emojis.length})
              </button>
              {categories.map(([slug, count]) => (
                <button
                  key={slug}
                  onClick={() => setFilterCategory(slug)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${
                    filterCategory === slug
                      ? 'bg-[#00E5CC] text-[#0d1f2d]'
                      : 'bg-muted hover:bg-muted/80 text-foreground'
                  }`}
                  data-testid={`filter-chip-${slug}`}
                >
                  {slug} ({count})
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-[#00A896]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              <Smile className="h-8 w-8 mx-auto mb-2 opacity-40" />
              {emojis.length === 0
                ? 'No emojis yet — upload some above.'
                : 'No emojis match the current filter.'}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="admin-emoji-grid">
              {filtered.map((e) => {
                const isSelected = selectedIds.has(e.emoji_id);
                const isEditing = editingId === e.emoji_id;
                return (
                  <div
                    key={e.emoji_id}
                    className={`group relative border rounded-md p-2 transition ${
                      isSelected ? 'border-[#00E5CC] bg-[#00E5CC]/10' : 'border-border bg-muted/30 hover:bg-muted/50'
                    }`}
                    data-testid={`admin-emoji-tile-${e.emoji_id}`}
                  >
                    {/* Multi-select checkbox */}
                    <label
                      className="absolute top-1 left-1 z-10 bg-background/80 rounded p-0.5 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                      title="Select for bulk delete"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          setSelectedIds((prev) => {
                            const n = new Set(prev);
                            n.has(e.emoji_id) ? n.delete(e.emoji_id) : n.add(e.emoji_id);
                            return n;
                          });
                        }}
                        className="h-3 w-3"
                        data-testid={`admin-emoji-select-${e.emoji_id}`}
                      />
                    </label>

                    {/* Image */}
                    <div className="aspect-square bg-background/50 rounded mb-2 flex items-center justify-center overflow-hidden">
                      {e.image_path ? (
                        <img
                          src={e.image_path?.startsWith('http') ? e.image_path : `${API}${e.image_path}`}
                          alt={e.label}
                          className="w-full h-full object-contain"
                          loading="lazy"
                          onError={(ev) => {
                            ev.currentTarget.style.display = 'none';
                            const fb = ev.currentTarget.nextElementSibling;
                            if (fb) fb.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="hidden w-full h-full items-center justify-center text-[10px] text-amber-700 dark:text-amber-300 bg-amber-500/10 px-1 text-center">
                        <AlertTriangle className="h-3 w-3 mr-1 inline" /> file missing
                      </div>
                    </div>

                    {/* Body — edit form OR static info */}
                    {isEditing ? (
                      <div className="space-y-1.5">
                        <Input
                          value={editDraft.label}
                          onChange={(ev) => setEditDraft({ ...editDraft, label: ev.target.value })}
                          className="text-xs h-7"
                          placeholder="label"
                          data-testid={`admin-emoji-edit-label-${e.emoji_id}`}
                        />
                        <Input
                          value={editDraft.categories}
                          onChange={(ev) => setEditDraft({ ...editDraft, categories: ev.target.value })}
                          className="text-xs h-7"
                          placeholder="categories (comma-separated)"
                          data-testid={`admin-emoji-edit-categories-${e.emoji_id}`}
                        />
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            className="h-6 px-2 text-[10px] flex-1"
                            onClick={() => handleSaveEdit(e)}
                            data-testid={`admin-emoji-save-${e.emoji_id}`}
                          >
                            <Save className="h-2.5 w-2.5 mr-1" /> Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-[10px]"
                            onClick={() => setEditingId(null)}
                            data-testid={`admin-emoji-cancel-${e.emoji_id}`}
                          >
                            <XIcon className="h-2.5 w-2.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-medium truncate" title={e.label}>{e.label}</p>
                        <div className="flex flex-wrap gap-1 mt-1 min-h-[18px]">
                          {(e.categories || []).slice(0, 3).map((c) => (
                            <Badge
                              key={c}
                              variant="secondary"
                              className="text-[9px] py-0 px-1.5 font-normal cursor-pointer"
                              onClick={() => setFilterCategory(c)}
                              title="Filter by this category"
                            >
                              {c}
                            </Badge>
                          ))}
                        </div>
                        {(e.excluded_platforms || []).length > 0 && (
                          <p className="text-[9px] font-semibold text-amber-700 dark:text-amber-400 mt-0.5">
                            Excludes {e.excluded_platforms.length} platform{e.excluded_platforms.length === 1 ? '' : 's'}
                          </p>
                        )}
                        <div className="flex gap-1 mt-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-5 px-1.5 text-[10px] text-[#00A896]"
                            onClick={() => setPreviewEmoji(e)}
                            title="Preview on every platform + exclusion toggles"
                            data-testid={`admin-emoji-preview-${e.emoji_id}`}
                          >
                            <Sparkles className="h-2.5 w-2.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-5 px-1.5 text-[10px]"
                            onClick={() => {
                              setEditingId(e.emoji_id);
                              setEditDraft({
                                label: e.label || '',
                                categories: (e.categories || []).join(', '),
                              });
                            }}
                            data-testid={`admin-emoji-edit-${e.emoji_id}`}
                          >
                            <Edit3 className="h-2.5 w-2.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-5 px-1.5 text-[10px] text-red-500 ml-auto"
                            onClick={() => handleDelete(e)}
                            data-testid={`admin-emoji-delete-${e.emoji_id}`}
                          >
                            <Trash2 className="h-2.5 w-2.5" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview-on-every-platform dialog */}
      <EmojiPlatformPreviewDialog
        emoji={previewEmoji}
        open={!!previewEmoji}
        onOpenChange={(o) => { if (!o) setPreviewEmoji(null); }}
        onSaved={(emojiId, excluded) => {
          setEmojis((arr) => arr.map((x) =>
            x.emoji_id === emojiId ? { ...x, excluded_platforms: excluded } : x,
          ));
        }}
      />

      {/* Batch auto-detect dialog — runs the per-emoji heuristic in
          parallel across every selected emoji, then offers a one-click
          "Apply all suggestions" that fires PUTs for every flagged
          emoji and updates the local grid. */}
      <BatchAutoDetectDialog
        open={batchAutoOpen}
        onOpenChange={setBatchAutoOpen}
        emojis={emojis.filter((e) => selectedIds.has(e.emoji_id))}
        onApplied={(updates) => {
          // updates: {emoji_id: [excluded_platforms]}
          setEmojis((arr) => arr.map((x) =>
            updates[x.emoji_id]
              ? { ...x, excluded_platforms: updates[x.emoji_id] }
              : x,
          ));
        }}
      />
    </div>
  );
};

export default EmojiManagerTab;
